import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog"
import { cn } from "@/lib/utils";
import { Stage, Layer, Image as KonvaImage } from "react-konva";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Textarea } from "@/shared/ui/textarea";
import { useEffect, useRef, useState } from "react";
import {
  useCreateNewTemplateImage,
  useGetSingleImage,
  useSaveImageEdit,
  useSaveImageTemplate,
  useUndoEditImage
} from "../../api/templates";
import { AlignCenter, AlignLeft, AlignRight, ArrowUp, Download, ImagePlus, Loader2, Square, Trash2, Type, Undo } from "lucide-react";
import { Slider } from "@/shared/ui/slider";
import TextLayer, { TextLayerAttr } from "@/shared/layer-editor/TextLayer";
import ImageLayer, { ImageAttr } from "@/shared/layer-editor/ImageLayer";
import Konva from "konva";
import InputColor from "@/shared/inputs/ColorInput";
import { queryClient } from "@/lib/react-query";
import CanvasImageLoader from "@/shared/loaders/canvas-image-loader";
import RectLayer, { RectLayerAttr } from "@/shared/layer-editor/ShapeLayer";

type Props = {
  trigger: JSX.Element
  image_id: number,
  isCarousel: boolean,
  image_url: string,
  event_id: number,
  template_id: number,
  post_image_id: number,
  openTextEditor: boolean,
  setOpen: (val: boolean) => void
}

function dataURLtoFile(dataUrl: string): File {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";

  const bstr = atob(arr[1]); // decode base64
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], "template_image", { type: mime });
}

type LayerType = 'image' | 'text' | 'shape';

type EditorLayer =
  | ({ type: 'image' } & ImageAttr)
  | ({ type: 'text' } & TextLayerAttr)
  | ({ type: 'shape' } & RectLayerAttr);


const EditTextDialog = ({ event_id, image_id, template_id, trigger, image_url, post_image_id, setOpen, openTextEditor }: Props) => {

  const hasImageRegeneratedRef = useRef(false)

  const [isBaseImageLoading, setIsBaseIMageLoading] = useState(false)

  const [templateImage, setTemplateImage] = useState<{ image_id: number, image_url: string } | undefined>({ image_url, image_id })

  const { imageData } = useGetSingleImage(templateImage?.image_id)

  const [img, setImg] = useState<HTMLImageElement | null>(null);

  const { status, generateNewTemplate, generatedTemplate } = useCreateNewTemplateImage((id: number, url: string) => setTemplateImage({ image_id: id, image_url: url }))

  const mainImageRef = useRef<Konva.Image>(null);

  // Filters for the main image
  const [filters, setFilters] = useState({
    brightness: 0,
    contrast: 0,
    saturation: 0,
  });

  const [layers, setLayers] = useState<EditorLayer[]>([]);

  // Deselect when clicking on empty area
  const checkDeselect = (e: any) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      setSelectionType(null);
    }
  };

  const addShapeLayer = () => {
    const newLayer: EditorLayer = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      color: "#ffffff",
      width: 50,
      height: 50,
      corderRadius: 0,
      opacity: 1,
      x: 50,
      y: 50,
    };
    setLayers(prev => [...prev, newLayer]);
    setSelectedId(newLayer.id);
    setSelectionType('shape');
  };

  const addTextLayer = () => {
    const newLayer: EditorLayer = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: "New Text",
      font: "Poppins",
      fontSize: 30,
      color: "#ffffff",
      textSpacing: 1,
      fontWeight: "normal",
      textAlign: "left",
      lineHeight: 1.2,
      textWidth: 300,
      x: 50,
      y: 50,
    };
    setLayers(prev => [...prev, newLayer]);
    setSelectedId(newLayer.id);
    setSelectionType('text');
  };

  // Delete selected text layer

  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 500,
    height: 500,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectionType, setSelectionType] = useState<'text' | 'image' | 'shape' | null>('text');

  const selectedLayer = layers.find(l => l.id === selectedId);
  const selectedTextLayer = selectedLayer?.type === 'text' ? selectedLayer : null;
  const selectedShapeLayer = selectedLayer?.type === 'shape' ? selectedLayer : null;

  const handleDeleteLayer = () => {
    if (!selectedId) return;
    setLayers(prev => prev.filter(layer => layer.id !== selectedId));
    setSelectedId(null);
    setSelectionType(null);
  };

  const handleBringToTop = () => {
    if (!selectedId) return;
    setLayers(prev => {
      const selected = prev.find(l => l.id === selectedId);
      if (!selected) return prev;
      const others = prev.filter(l => l.id !== selectedId);
      return [...others, selected];
    });
  };

  const updateTextLayer = (updates: Partial<TextLayerAttr>) => {
    if (!selectedId) return;
    setLayers(prev =>
      prev.map(l => (l.id === selectedId && l.type === 'text' ? { ...l, ...updates } : l))
    );
  };

  const updateShapeLayer = (updates: Partial<RectLayerAttr>) => {
    if (!selectedId) return;
    setLayers(prev =>
      prev.map(l => (l.id === selectedId && l.type === 'shape' ? { ...l, ...updates } : l))
    );
  };
  const stageRef = useRef<any>(null);

  const updateFilter = (key: keyof typeof filters, value: number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (generatedTemplate) {
      setTemplateImage({ image_url: generatedTemplate.data.image_url, image_id: generatedTemplate.data.image_id })
      queryClient.invalidateQueries({ queryKey: ["single-image"] })
    }
  }, [generatedTemplate])

  useEffect(() => {
    if (imageData) {
      const image = new window.Image();
      image.src = imageData.image_url; // replace with uploaded image later
      image.crossOrigin = "anonymous";
      image.onload = () => {
        setImg(image);
        setTemplateImage({ image_url: imageData.image_url, image_id: imageData.image_id })
        setDimensions({ width: image.width / 2, height: image.height / 2 });
        setIsBaseIMageLoading(false)
      };
    }
  }, [imageData]);

  const { uploadTemplate, saveTemplateStatus } = useSaveImageTemplate()

  const { saveImageEdit, status: saveImageStatus } = useSaveImageEdit()

  const { undoImage, undoStatus } = useUndoEditImage()

  const undoImageHandler = () => {
    if (templateImage) {
      undoImage(templateImage.image_id)
    }
  }

  const updateImageTemplate = () => {
    if (stageRef.current && templateImage) {

      setSelectedId(null);
      setSelectionType(null);

      setTimeout(() => {
        const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
        const file = dataURLtoFile(uri);
        if (hasImageRegeneratedRef.current) {
          uploadTemplate({
            image: file,
            image_id: post_image_id,
            event_id: event_id
          }).then(() => {
            hasImageRegeneratedRef.current = false
            setOpen(false)
          })
        } else {
          saveImageEdit({
            image_id: post_image_id,
            image_file: uri
          }).then(() => {
            setOpen(false)
          })
        }
      }, 100);

      // const link = document.createElement("a");
      // link.download = "edited-image.png";
      // link.href = uri;
      // link.click();
    }
  };



  useEffect(() => {
    const node = mainImageRef.current;
    if (node) {
      node.cache();
      node.filters([
        Konva.Filters.Brighten,
        Konva.Filters.Contrast,
        Konva.Filters.HSL,
      ]);
      node.brightness(filters.brightness);
      node.contrast(filters.contrast);
      node.saturation(filters.saturation);
      node.getLayer()?.batchDraw();
    }
  }, [filters]);

  const downloadImage = () => {

    if (stageRef.current && image_id) {
      // Deselect all before saving
      setSelectedId(null);
      setSelectionType(null);

      // Wait for transformers to hide
      setTimeout(() => {
        const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
        const link = document.createElement("a");
        link.download = "edited-image.png";
        link.href = uri;
        link.click();
      }, 100);
    }

  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const image = new window.Image();
      image.src = event.target?.result as string;
      image.onload = () => {
        const maxSize = 200;
        const scale = Math.min(maxSize / image.width, maxSize / image.height);

        const newImage: EditorLayer = {
          id: `img-${Date.now()}`,
          type: 'image',
          image,
          x: 50,
          y: 50,
          width: image.width * scale,
          height: image.height * scale,
          originalWidth: image.width,
          originalHeight: image.height,
        };

        setLayers(prev => [...prev, newImage]);
        setSelectedId(newImage.id);
        setSelectionType('image');
      };
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getLayersByType = (type: LayerType) => layers.filter(layer => layer.type === type);

  return (
    <Dialog open={openTextEditor} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-5xl flex flex-col gap-2 pt-5 h-[90vh] overflow-hidden !rounded-3xl">
        <DialogHeader>
          <DialogTitle>Edit Image</DialogTitle>
        </DialogHeader>
        <div className="w-full flex flex-col lg:flex-row overflow-auto h-full">
          <section className='w-full lg:w-4/5 lg:h-full min-h-[90%] p-4 relative overflow-hidden' id="editor-text">
            <div
              className={cn(
                "absolute inset-0",
                "[background-size:30px_30px]",
                "[background-image:radial-gradient(#5512ff_1.2px,transparent_1px)]",
                "dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]",
              )}
            />

            {/* Toolbar */}
            <div className={cn("absolute top-[50%] -translate-y-[50%] backdrop-blur flex flex-col gap-2 py-3 px-2 border border-purple-400 rounded-full",
            )}>
              <Button
                onClick={addShapeLayer}
                variant="ghost"
                size="icon">
                <Square />
              </Button>
              <Button
                onClick={addTextLayer}
                variant="ghost"
                size="icon">
                <Type />
              </Button>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="ghost" size="icon"><ImagePlus /></Button>

              <Button
                type="button"
                onClick={handleBringToTop}
                variant="ghost"
                size="icon"
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                onClick={handleDeleteLayer}
                variant="ghost"
                size="icon"
              >
                <Trash2 className="text-red-500" />
              </Button>


              <Button variant="ghost" size="icon" onClick={downloadImage}>
                <Download />
              </Button>

              <Button
                onClick={undoImageHandler}
                variant="ghost"
                size="icon">
                {undoStatus === "pending"
                  ? <Loader2 className="animate-spin" />
                  : <Undo />

                }
              </Button>

            </div>

            {/* Canvas */}
            <div className="w-fit mx-auto pt-12 relative">
              {isBaseImageLoading &&
                <CanvasImageLoader />
              }
              <Stage
                width={dimensions.width}
                height={dimensions.height}
                ref={stageRef}
                onMouseDown={checkDeselect}
                onTouchStart={checkDeselect}
                className="border"
              >
                <Layer>
                  {img && (
                    <KonvaImage
                      ref={mainImageRef}
                      image={img}
                      width={dimensions.width}
                      height={dimensions.height}
                      onClick={() => {
                        setSelectedId(null);
                        setSelectionType(null);
                      }}
                    />
                  )}

                  {layers.map((layer, i) => {
                    if (layer.type === 'image') {
                      return (
                        <ImageLayer
                          key={layer.id}
                          imageAttr={layer}
                          isSelected={layer.id === selectedId}
                          onSelect={() => {
                            setSelectedId(layer.id);
                            setSelectionType('image');
                          }}
                          onChange={(newAttrs) => {
                            const updated = { ...layer, ...newAttrs };
                            const newLayers = layers.slice();
                            newLayers[i] = updated;
                            setLayers(newLayers);
                          }}
                        />
                      );
                    }

                    if (layer.type === 'text') {
                      return (
                        <TextLayer
                          key={layer.id}
                          textAttr={layer}
                          isSelected={layer.id === selectedId}
                          onSelect={() => {
                            setSelectedId(layer.id);
                            setSelectionType('text');
                          }}
                          onChange={(newAttrs) => {
                            const updated = { ...layer, ...newAttrs };
                            const newLayers = layers.slice();
                            newLayers[i] = updated;
                            setLayers(newLayers);
                          }}
                        />
                      );
                    }

                    if (layer.type === 'shape') {
                      return (
                        <RectLayer
                          key={layer.id}
                          shapeAttr={layer}
                          isSelected={layer.id === selectedId}
                          onSelect={() => {
                            setSelectedId(layer.id);
                            setSelectionType('shape');
                          }}
                          onChange={(newAttrs) => {
                            const updated = { ...layer, ...newAttrs };
                            const newLayers = layers.slice();
                            newLayers[i] = updated;
                            setLayers(newLayers);
                          }}
                        />
                      );
                    }

                    return null;
                  })}
                </Layer>
              </Stage>

            </div>
          </section>
          <section
            className='w-full lg:w-1/5 bg-gradient-to-b from-white to-purple-50 !mt-0 border-s-indigo-300 border-s h-full p-2 flex flex-col gap-2' id="editor-siai">

            <div className="flex items-center justify-between">
              <p className="font-montserrat tracking-wide font-semibold text-purple-500">siAi Image Canvas</p>
            </div>

            {/* Text layer selector */}

            {getLayersByType('text').length > 1 && (
              <div className="flex gap-2 flex-wrap pb-2 border-b border-purple-200">
                {getLayersByType('text').map((layer, index) => (
                  <Button
                    key={layer.id}
                    type="button"
                    size="sm"
                    variant={selectedId === layer.id && selectionType === 'text' ? "default" : "outline"}
                    onClick={() => {
                      setSelectedId(layer.id);
                      setSelectionType('text');
                    }}
                    className={cn(
                      "h-7 text-xs",
                      selectedId === layer.id && selectionType === 'text' && "bg-gradient-to-r from-purple-500 to-indigo-500"
                    )}
                  >
                    Text {index + 1}
                  </Button>
                ))}
              </div>
            )}


            {!selectedTextLayer && !selectedShapeLayer && (
              <>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Brightness</Label>
                  <Slider
                    value={[filters.brightness]}
                    onValueChange={([val]) => updateFilter("brightness", val)}
                    min={-1}
                    max={1}
                    step={0.005}
                    className="w-full"
                  />
                </div>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Contrast</Label>
                  <Slider
                    min={-100}
                    max={100}
                    step={1}
                    value={[filters.contrast]}
                    onValueChange={([val]) => updateFilter("contrast", val)}
                    className="w-full"
                  />
                </div>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Saturation</Label>
                  <Slider
                    min={-2}
                    max={2}
                    step={0.1}
                    value={[filters.saturation]}
                    onValueChange={([val]) => updateFilter("saturation", val)}
                    className="w-full"
                  />
                </div>
              </>


            )}

            {selectedShapeLayer && (
              <>
                <div className="w-full flex items-center gap-1 px-2">
                  <InputColor
                    label=""
                    value={selectedShapeLayer.color}
                    onChange={(val: string) => updateShapeLayer({ color: val })}
                    onBlur={() => console.log("color picker")}
                  />
                </div>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Border Radius</Label>
                  <Slider
                    min={0}
                    max={50}
                    step={1}
                    value={[selectedShapeLayer.corderRadius]}
                    onValueChange={([val]) => updateShapeLayer({ corderRadius: val })}
                    className="w-full"
                  />
                </div>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Opacity</Label>
                  <Slider
                    min={0}
                    max={1}
                    step={0.1}
                    value={[selectedShapeLayer.opacity]}
                    onValueChange={([val]) => updateShapeLayer({ opacity: val })}
                    className="w-full"
                  />
                </div>

              </>
            )}


            {selectedTextLayer && (
              <>
                <Label className="text-sm font-poppins font-normal">Input Text</Label>
                <Textarea
                  value={selectedTextLayer.text}
                  onChange={e => updateTextLayer({ text: e.target.value })}
                  className="resize-none"
                />

                <div className="flex gap-2">
                  <Select
                    value={selectedTextLayer.font}
                    onValueChange={(val: string) => updateTextLayer({ font: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a font" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Fonts</SelectLabel>
                        <SelectItem value="Poppins" className="text-xs">Poppins</SelectItem>
                        <SelectItem value="Roboto" className="text-xs">Roboto</SelectItem>
                        <SelectItem value="Montserrat" className="text-xs">Montserrat</SelectItem>
                        <SelectItem value="Arial" className="text-xs">Arial</SelectItem>
                        <SelectItem value="Courier" className="text-xs">Courier</SelectItem>
                        <SelectItem value="Times New Roman" className="text-xs">Times New Roman</SelectItem>
                        <SelectItem value="Noto Serif" className="text-xs">Noto Serif</SelectItem>
                        <SelectItem value="Noto Sans" className="text-xs">Noto Sans</SelectItem>
                        <SelectItem value="Lato" className="text-xs">Lato</SelectItem>
                        <SelectItem value="Playfair Display" className="text-xs">Playfair Display</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={selectedTextLayer.fontSize}
                    onChange={(e) => updateTextLayer({ fontSize: Number(e.target.value) })}
                    className="w-1/2"
                  />
                </div>

                <div className="flex lg:flex-col 2xl:flex-row  gap-2">
                  <div className="flex gap-2 px-2">
                    <Button
                      type="button"
                      size="icon"
                      variant={selectedTextLayer.textAlign === "left" ? "default" : "outline"}
                      onClick={() => updateTextLayer({ textAlign: "left" })}
                      className={cn("w-7 h-7", selectedTextLayer.textAlign === "left" && "bg-gradient-to-r from-purple-500 to-indigo-500")}
                    >
                      <AlignLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant={selectedTextLayer.textAlign === "center" ? "default" : "outline"}
                      onClick={() => updateTextLayer({ textAlign: "center" })}
                      className={cn("w-7 h-7", selectedTextLayer.textAlign === "center" && "bg-gradient-to-r from-purple-500 to-indigo-500 text-white")}
                    >
                      <AlignCenter className="w-5 h-5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant={selectedTextLayer.textAlign === "right" ? "default" : "outline"}
                      onClick={() => updateTextLayer({ textAlign: "right" })}
                      className={cn("w-7 h-7", selectedTextLayer.textAlign === "right" && "bg-gradient-to-r from-purple-500 to-indigo-500 text-white")}
                    >
                      <AlignRight className="w-5 h-5" />
                    </Button>
                  </div>
                  <Select
                    value={selectedTextLayer.fontWeight}
                    onValueChange={(val: string) => updateTextLayer({ fontWeight: val as "normal" | "medium" | "600" | "bold" })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font weight" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Weight</SelectLabel>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="600">Semibold</SelectItem>
                        <SelectItem value="bold">Bold</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                </div>

                <div className="w-full flex items-center gap-1 px-2">
                  <InputColor
                    label=""
                    value={selectedTextLayer.color}
                    onChange={(val: string) => updateTextLayer({ color: val })}
                    onBlur={() => console.log("color picker")}
                  />
                </div>
                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Letter Spacing</Label>
                  <Slider
                    value={[selectedTextLayer.textSpacing]}
                    onValueChange={(val) => updateTextLayer({ textSpacing: val[0] })}
                    min={0.5}
                    max={3}
                    step={0.1}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500 text-right">{selectedTextLayer.textSpacing.toFixed(1)}</span>
                </div>

                <div className="w-full flex flex-col gap-2 px-2">
                  <Label className="text-sm font-poppins font-normal">Line Height</Label>
                  <Slider
                    value={[selectedTextLayer.lineHeight]}
                    onValueChange={(val) => updateTextLayer({ lineHeight: val[0] })}
                    min={0.5}
                    max={3}
                    step={0.1}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500 text-right">{selectedTextLayer.lineHeight.toFixed(1)}</span>
                </div>


              </>
            )}



            <Label className="text-xs pt-4 font-poppins font-normal ps-2">Generate New Template</Label>
            <Button
              onClick={() => {
                hasImageRegeneratedRef.current = true
                setIsBaseIMageLoading(true)
                generateNewTemplate({
                  template_id: template_id,
                  event_id: event_id
                })
              }}
              className="rounded-full border border-purple-500 text-sm" variant="outline">
              {status === "pending" ? <Loader2 className="animate-spin" /> : "Generate"}
            </Button>
          </section>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button className="rounded-full border border-purple-500" variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={(saveTemplateStatus === "pending" || saveImageStatus === "pending")}
            onClick={updateImageTemplate}
            className="rounded-full border border-purple-500"
            variant="outline">
            {(saveTemplateStatus === "pending" || saveImageStatus === "pending")
              ? <Loader2 className="animate-spin" />
              : "Save"
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditTextDialog;
